import * as React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { Loader, tokens } from '@hotjar/hotsauce';
import type { Highlight } from '@hotjar/webapp/domains/highlights/model';

import { HeatmapImage } from '../Heatmaps/HeatmapImage';
import { RecordingHighlightContent } from './RecordingHighlightContent';

const LoadingContentWrapper = styled.section`
  color: ${tokens.color.text_default_inverse};
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledLoader = styled(Loader)`
  margin-bottom: ${tokens.spacer.x1};
`;

interface HighlightContentProps {
  className?: string;
  highlight?: Highlight;
  siteId: string | null;
  svgRef: React.ComponentProps<typeof HeatmapImage>['ref'];
}

export function HighlightContent({
  className,
  highlight,
  siteId,
  svgRef,
}: HighlightContentProps): JSX.Element | null {
  const { t } = useTranslation();

  if (!highlight) {
    return (
      <LoadingContentWrapper>
        <StyledLoader size="l" />
        {t('highlights:single.loading')}
      </LoadingContentWrapper>
    );
  }

  if (highlight.type === 'heatmap') {
    return (
      <HeatmapImage
        className={className}
        url={highlight.imageUrl}
        selectedArea={highlight.metadata.selectedArea}
        clicksCount={highlight.metadata.clicksCount ?? 0}
        clicksPercentage={highlight.metadata.clicksPercentage ?? 0}
        isDesktop={highlight.metadata.deviceType === 'desktop'}
        ref={svgRef}
      />
    );
  }

  if (highlight.type === 'recording') {
    return (
      <RecordingHighlightContent className={className} highlight={highlight} siteId={siteId} />
    );
  }

  return null;
}
